import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { NotebookWorkingCopyTypeIdentifier, NotebookSetting, CellUri } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { NotebookFileWorkingCopyModelFactory, SimpleNotebookEditorModel } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookEditorModel';
import { ReferenceCollection, DisposableStore, dispose, combinedDisposable, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { INotebookService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookService.service';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { Emitter, AsyncEmitter } from 'vscode/vscode/vs/base/common/event';
import { IExtensionService } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions.service';
import { IUriIdentityService } from 'vscode/vscode/vs/platform/uriIdentity/common/uriIdentity.service';
import { ResourceMap } from 'vscode/vscode/vs/base/common/map';
import { FileWorkingCopyManager } from '../../../services/workingCopy/common/fileWorkingCopyManager.js';
import { Schemas } from 'vscode/vscode/vs/base/common/network';
import { NotebookProviderInfo } from './notebookProvider.js';
import { assertIsDefined } from 'vscode/vscode/vs/base/common/types';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';

let NotebookModelReferenceCollection = class NotebookModelReferenceCollection extends ReferenceCollection {
    constructor(_instantiationService, _notebookService, _logService, _configurationService) {
        super();
        this._instantiationService = _instantiationService;
        this._notebookService = _notebookService;
        this._logService = _logService;
        this._configurationService = _configurationService;
        this._disposables = ( new DisposableStore());
        this._workingCopyManagers = ( new Map());
        this._modelListener = ( new Map());
        this._onDidSaveNotebook = ( new Emitter());
        this.onDidSaveNotebook = this._onDidSaveNotebook.event;
        this._onDidChangeDirty = ( new Emitter());
        this.onDidChangeDirty = this._onDidChangeDirty.event;
        this._dirtyStates = ( new ResourceMap());
        this.modelsToDispose = ( new Set());
    }
    dispose() {
        this._disposables.dispose();
        this._onDidSaveNotebook.dispose();
        this._onDidChangeDirty.dispose();
        dispose(( this._modelListener.values()));
        dispose(( this._workingCopyManagers.values()));
    }
    isDirty(resource) {
        return this._dirtyStates.get(resource) ?? false;
    }
    async createReferencedObject(key, viewType, hasAssociatedFilePath, limits) {
        this.modelsToDispose.delete(key);
        const uri = ( URI.parse(key));
        const workingCopyTypeId = NotebookWorkingCopyTypeIdentifier.create(viewType);
        let workingCopyManager = this._workingCopyManagers.get(workingCopyTypeId);
        if (!workingCopyManager) {
            const factory = ( new NotebookFileWorkingCopyModelFactory(viewType, this._notebookService, this._configurationService));
            workingCopyManager = this._instantiationService.createInstance(FileWorkingCopyManager, workingCopyTypeId, factory, factory);
            this._workingCopyManagers.set(workingCopyTypeId, workingCopyManager);
        }
        const scratchPad = viewType === 'interactive' && this._configurationService.getValue(NotebookSetting.InteractiveWindowPromptToSave) !== true;
        const model = this._instantiationService.createInstance(SimpleNotebookEditorModel, uri, hasAssociatedFilePath, viewType, workingCopyManager, scratchPad);
        const result = await model.load({ limits });
        let onDirtyAutoReference;
        this._modelListener.set(result, combinedDisposable(result.onDidSave(() => this._onDidSaveNotebook.fire(result.resource)), result.onDidChangeDirty(() => {
            const isDirty = result.isDirty();
            this._dirtyStates.set(result.resource, isDirty);
            if (isDirty && !onDirtyAutoReference) {
                onDirtyAutoReference = this.acquire(key, viewType);
            }
            else if (onDirtyAutoReference) {
                onDirtyAutoReference.dispose();
                onDirtyAutoReference = undefined;
            }
            this._onDidChangeDirty.fire(result);
        }), toDisposable(() => onDirtyAutoReference?.dispose())));
        return result;
    }
    destroyReferencedObject(key, object) {
        this.modelsToDispose.add(key);
        (async () => {
            try {
                const model = await object;
                if (!( this.modelsToDispose.has(key))) {
                    return;
                }
                if (model instanceof SimpleNotebookEditorModel) {
                    await model.canDispose();
                }
                if (!( this.modelsToDispose.has(key))) {
                    return;
                }
                this._modelListener.get(model)?.dispose();
                this._modelListener.delete(model);
                model.dispose();
            }
            catch (err) {
                this._logService.error('FAILED to destory notebook', err);
            }
            finally {
                this.modelsToDispose.delete(key);
            }
        })();
    }
};
NotebookModelReferenceCollection = ( __decorate([
    ( __param(0, IInstantiationService)),
    ( __param(1, INotebookService)),
    ( __param(2, ILogService)),
    ( __param(3, IConfigurationService))
], NotebookModelReferenceCollection));
let NotebookModelResolverServiceImpl = class NotebookModelResolverServiceImpl {
    constructor(instantiationService, _notebookService, _extensionService, _uriIdentService) {
        this._notebookService = _notebookService;
        this._extensionService = _extensionService;
        this._uriIdentService = _uriIdentService;
        this._onWillFailWithConflict = ( new AsyncEmitter());
        this.onWillFailWithConflict = this._onWillFailWithConflict.event;
        this._data = instantiationService.createInstance(NotebookModelReferenceCollection);
        this.onDidSaveNotebook = this._data.onDidSaveNotebook;
        this.onDidChangeDirty = this._data.onDidChangeDirty;
    }
    dispose() {
        this._data.dispose();
    }
    isDirty(resource) {
        return this._data.isDirty(resource);
    }
    async resolve(arg0, viewType, limits) {
        let resource;
        let hasAssociatedFilePath = false;
        if (URI.isUri(arg0)) {
            resource = arg0;
        }
        else {
            if (!arg0.untitledResource) {
                const info = this._notebookService.getContributedNotebookType(assertIsDefined(viewType));
                if (!info) {
                    throw ( new Error('UNKNOWN view type: ' + viewType));
                }
                const suffix = NotebookProviderInfo.possibleFileEnding(info.selectors) ?? '';
                for (let counter = 1;; counter++) {
                    const candidate = ( URI.from(
                        { scheme: Schemas.untitled, path: `Untitled-${counter}${suffix}`, query: viewType }
                    ));
                    if (!this._notebookService.getNotebookTextModel(candidate)) {
                        resource = candidate;
                        break;
                    }
                }
            }
            else if (arg0.untitledResource.scheme === Schemas.untitled) {
                resource = arg0.untitledResource;
            }
            else {
                resource = arg0.untitledResource.with({ scheme: Schemas.untitled });
                hasAssociatedFilePath = true;
            }
        }
        if (resource.scheme === CellUri.scheme) {
            throw ( new Error(`CANNOT open a cell-uri as notebook. Tried with ${( resource.toString())}`));
        }
        resource = this._uriIdentService.asCanonicalUri(resource);
        const existingViewType = this._notebookService.getNotebookTextModel(resource)?.viewType;
        if (!viewType) {
            if (existingViewType) {
                viewType = existingViewType;
            }
            else {
                await this._extensionService.whenInstalledExtensionsRegistered();
                const providers = this._notebookService.getContributedNotebookTypes(resource);
                const exclusiveProvider = providers.find(provider => provider.exclusive);
                viewType = exclusiveProvider?.id || providers[0]?.id;
            }
        }
        if (!viewType) {
            throw ( new Error(`Missing viewType for '${resource}'`));
        }
        if (existingViewType && existingViewType !== viewType) {
            await this._onWillFailWithConflict.fireAsync({ resource, viewType }, CancellationToken.None);
            const existingViewType2 = this._notebookService.getNotebookTextModel(resource)?.viewType;
            if (existingViewType2 && existingViewType2 !== viewType) {
                throw ( new Error(
                    `A notebook with view type '${existingViewType2}' already exists for '${resource}', CANNOT create another notebook with view type ${viewType}`
                ));
            }
        }
        const reference = this._data.acquire(( resource.toString()), viewType, hasAssociatedFilePath, limits);
        try {
            const model = await reference.object;
            return {
                object: model,
                dispose() { reference.dispose(); }
            };
        }
        catch (err) {
            reference.dispose();
            throw err;
        }
    }
};
NotebookModelResolverServiceImpl = ( __decorate([
    ( __param(0, IInstantiationService)),
    ( __param(1, INotebookService)),
    ( __param(2, IExtensionService)),
    ( __param(3, IUriIdentityService))
], NotebookModelResolverServiceImpl));

export { NotebookModelResolverServiceImpl };
